import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Permission from "../Permission";
import usePermissions from "../../hooks/usePermissions";
import { Wallet, Eye, EyeOff, AlertCircle, ArrowRight, RefreshCw } from "lucide-react";
import {
  fetchUserWallet,
  selectWallets,
  selectAvailableBalance,
  selectWalletLoading,
  selectWalletError,
} from "../../store/slices/walletSlice";

// Helpers
const formatUsdt = (n) => {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0.00";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const shortenAddress = (addr) => {
  if (!addr) return "—";
  const s = String(addr);
  return s.length > 20 ? `${s.slice(0, 8)}…${s.slice(-8)}` : s;
};

// The wallet Redux slice historically stored either a number OR the whole
// `data.data` object under `availableBalance` (see walletSlice.js line 43).
// Normalize to a number here so this page never displays NaN.
const pickAvailableBalance = (raw, wallets) => {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (raw && typeof raw === "object" && typeof raw.availableBalance === "number") {
    return raw.availableBalance;
  }
  // Fallback: sum the per-network balances
  return (wallets || []).reduce((sum, w) => sum + (Number(w?.balance) || 0), 0);
};

const WalletBalancePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSubUser, userRole } = usePermissions();

  const walletData = useSelector(selectWallets);
  const rawAvailableBalance = useSelector(selectAvailableBalance);
  const loading = useSelector(selectWalletLoading);
  const error = useSelector(selectWalletError);

  useEffect(() => {
    dispatch(fetchUserWallet());
  }, [dispatch]);

  const trc20 = walletData?.find((w) => w.walletNetwork === "USDT-TRC20");
  const erc20 = walletData?.find((w) => w.walletNetwork === "USDT-ERC20");
  const trc20Balance = Number(trc20?.balance || 0);
  const erc20Balance = Number(erc20?.balance || 0);
  const availableBalance = pickAvailableBalance(rawAvailableBalance, walletData);

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-semibold text-gray-800 flex items-center">
            <Wallet className="w-8 h-8 mr-3 text-blue-600" />
            Wallet Balances
          </h2>
          <p className="text-gray-600 mt-2">
            Live balances from your merchant wallets on-chain.
          </p>

          {isSubUser && (
            <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-blue-500 mr-2" />
                <span className="text-blue-700 font-medium">
                  Sub-User Account ({userRole?.replace("_", " ").toUpperCase()})
                </span>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => dispatch(fetchUserWallet())}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
          title="Refresh balances"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="text-sm text-gray-700">Refresh</span>
        </button>
      </div>

      <Permission
        permission="balance-view"
        fallback={
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <EyeOff className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-red-800 mb-2">Access Restricted</h3>
            <p className="text-red-600 mb-4">
              You don't have permission to view balance information.
            </p>
            <div className="bg-red-100 rounded-lg p-4 text-left">
              <p className="text-sm text-red-700">
                <strong>Your Role:</strong> {userRole?.replace("_", " ").toUpperCase()}
              </p>
              <p className="text-sm text-red-700 mt-1">
                <strong>Required Permission:</strong> balance-view
              </p>
              {(userRole === "super_operation" || userRole === "operation") && (
                <p className="text-sm text-red-700 mt-2">
                  💡 <em>{userRole.replace("_", " ")} roles cannot view balance information by design.</em>
                </p>
              )}
            </div>
          </div>
        }
      >
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700 text-sm">
              Failed to load balances: {String(error)}
            </span>
          </div>
        )}

        <div className="space-y-6">
          {/* Summary — real numbers */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Available balance</p>
                  <p className="text-3xl font-bold">{formatUsdt(availableBalance)}</p>
                  <p className="text-blue-100 text-sm">USDT (all networks)</p>
                </div>
                <Wallet className="w-12 h-12 text-blue-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">USDT · TRC20</p>
                  <p className="text-3xl font-bold">{formatUsdt(trc20Balance)}</p>
                  <p className="text-green-100 text-sm truncate max-w-[220px]">
                    {shortenAddress(trc20?.walletAddress)}
                  </p>
                </div>
                <Eye className="w-12 h-12 text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">USDT · ERC20</p>
                  <p className="text-3xl font-bold">{formatUsdt(erc20Balance)}</p>
                  <p className="text-purple-100 text-sm truncate max-w-[220px]">
                    {shortenAddress(erc20?.walletAddress)}
                  </p>
                </div>
                <Eye className="w-12 h-12 text-purple-200" />
              </div>
            </div>
          </div>

          {/* Per-network detail */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Networks</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="py-3 pr-4">Network</th>
                    <th className="py-3 pr-4">Wallet address</th>
                    <th className="py-3 pr-4 text-right">On-chain balance</th>
                    <th className="py-3 pr-4 text-right">Accrued fees</th>
                  </tr>
                </thead>
                <tbody>
                  {[trc20, erc20].filter(Boolean).map((w) => (
                    <tr key={w.walletNetwork} className="border-t border-gray-100">
                      <td className="py-3 pr-4 font-medium text-gray-800">{w.walletNetwork}</td>
                      <td className="py-3 pr-4 font-mono text-gray-600">{shortenAddress(w.walletAddress)}</td>
                      <td className="py-3 pr-4 text-right font-mono">{formatUsdt(w.balance)} USDT</td>
                      {/* Backend returns "feeAmount"; also tolerate "feeAmt" from older builds. */}
                      <td className="py-3 pr-4 text-right font-mono text-gray-500">{formatUsdt(w.feeAmount ?? w.feeAmt)} USDT</td>
                    </tr>
                  ))}
                  {(!trc20 && !erc20) && (
                    <tr>
                      <td colSpan="4" className="py-6 text-center text-gray-500">
                        {loading ? "Loading wallets…" : "No wallets found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              On-chain balance is the total your merchant wallet holds. Accrued fees are the platform
              fees pending sweep — this amount is not withdrawable.
            </p>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Need to move funds?</h3>
              <p className="text-gray-600 text-sm">
                Send a payout from your available balance to any external wallet.
              </p>
            </div>
            <button
              onClick={() => navigate("/withdraw-requests")}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <span>Go to withdrawals</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Permission>
    </div>
  );
};

export default WalletBalancePage;
